import React, { useEffect } from "react"
import { graphql, useStaticQuery } from "gatsby"
import i18next from "i18next"
import { I18nextProvider, initReactI18next } from "react-i18next"
import LocaleContext from "../contexts/LocaleContext"
import cookies from "browser-cookies"

const otherTranslations = {
  "en-US": {
    calculator: require("./locales/en-US/calculator.json"),
    "quote-offers-4": require("./locales/en-US/quote-offers-4.json"),
    state: require("./locales/en-US/state.json"),
  },
  "es-MX": {
    calculator: require("./locales/es-MX/calculator.json"),
    "quote-offers-4": require("./locales/es-MX/quote-offers-4.json"),
    state: require("./locales/es-MX/state.json"),
  },
}

const COOKIE_DOMAIN = process.env.GATSBY_COOKIE_DOMAIN
const GOOGLE_TRANSLATE_COOKIE_NAME = "googtrans"

const useGoogleTranslateCookie = (language) => {
  useEffect(() => {
    const shortLang = language.substring(0, 2)
    if (COOKIE_DOMAIN) {
      const domainSplit = COOKIE_DOMAIN.split(".")
      if (domainSplit.length > 1) {
        domainSplit.splice(-2, 2, domainSplit.slice(-2).join("."))
      }
      const cookieDomains = domainSplit.map((_, index, domains) =>
        domains.slice(-index).join(".")
      )

      // TODO - we also need to (re)set the value of pure insurify.com domain cookie
      // because the code below follows the standard and only sets cookies for domains
      // starting with dot (.insurify.com)
      cookieDomains.forEach((domain) => {
        cookies.set(GOOGLE_TRANSLATE_COOKIE_NAME, `/en/${shortLang}`, {
          domain: domain,
          path: "/",
          expires: 0, // session
        })
      })
    }
  })
}

const addComponentLocales = (locales, i18n) => {
  locales.forEach(({ node }) => {
    if (!i18n.hasResourceBundle(node.language, node.ns)) {
      i18n.addResourceBundle(node.language, node.ns, JSON.parse(node.data))
    }
  })
}

const initI18n = (props, localeJsonNodeName, componentTranslations) => {
  const { data, pageContext } = props
  const { language, defaultLanguage } = pageContext.localization

  const localeNodes = data?.[localeJsonNodeName]
    ? data[localeJsonNodeName].edges || []
    : []

  const namespaces = localeNodes.map(({ node }) => node.ns)

  // Set the default namespace to a page namespace if it exists
  // and use other namespaces as fallback
  // (so that we skip specifying namespaces in pages)
  let defaultNS = "translation"
  defaultNS = namespaces.find((ns) => ns !== defaultNS) || defaultNS
  const fallbackNS = namespaces.filter((ns) => ns !== defaultNS)

  const i18n = i18next
    .createInstance({
      lng: language,
      fallbackLng: defaultLanguage,
      defaultNS,
      fallbackNS,
      initImmediate: false,
      react: {
        useSuspense: false,
      },
    })
    .use(initReactI18next)

  // noinspection JSIgnoredPromiseFromCall
  i18n.init()

  addComponentLocales(localeNodes, i18n)

  if (language === "es-MX") {
    i18n.addResourceBundle(
      "es-MX",
      "components",
      JSON.parse(componentTranslations.componentsEs.json)
    )
  } else {
    i18n.addResourceBundle(
      "en-US",
      "components",
      JSON.parse(componentTranslations.componentsEn.json)
    )
  }

  Object.keys(otherTranslations[language]).forEach((ns) => {
    i18n.addResourceBundle(language, ns, otherTranslations[language][ns])
  })

  if (i18n.language !== language) {
    i18n.changeLanguage(language)
  }

  return i18n
}

export const WrapWithI18nProvider = (
  { element, props },
  { localeJsonNodeName = "locale" }
) => {
  if (!props.pageContext.localization) {
    const pageLang = props.location.pathname.includes("/es/")
      ? "es-MX"
      : "en-US"
    props.pageContext.localization = {
      language: pageLang,
      defaultLanguage: pageLang,
      languages: [pageLang],
    }
  }

  useGoogleTranslateCookie(props.pageContext.localization.language)

  const componentTranslations = useStaticQuery(graphql`
    query {
      componentsEn: componentTranslation(lang: "en-US") {
        json
      }
      componentsEs: componentTranslation(lang: "es-MX") {
        json
      }
    }
  `)

  const i18n = initI18n(props, localeJsonNodeName, componentTranslations)

  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={props.pageContext.localization}>
        {element}
      </LocaleContext.Provider>
    </I18nextProvider>
  )
}
